import { type ComponentProps, useState } from 'react';
import clsx from 'clsx';
import { Box, Link, PlainButton, Stack, Text } from '@customerio/pluma-components/react';
import * as styles from './filter.css';

const VISIBLE_OPTIONS = 10;

export type FilterOption = {
  label: string;
  value: string;
};

type FilterProps = {
  options: FilterOption[];
  selected?: string;
  onChange: (value: string) => void;
} & Omit<ComponentProps<typeof Stack>, 'onChange'>;

/**
 * A single-select option list. Shows the first ten options with a Show
 * more/less toggle when there are more; a selected option past the tenth is
 * swapped into view so it stays visible while collapsed.
 */
export function Filter({ options, selected, onChange, ...rest }: FilterProps) {
  const [isShowingAll, setIsShowingAll] = useState(false);

  const hasOptionsToHide = options.length > VISIBLE_OPTIONS;
  const hasHiddenOptions = !isShowingAll && hasOptionsToHide;

  let visibleOptions = options;
  if (hasHiddenOptions) {
    const selectedIndex = selected != null ? options.findIndex((option) => option.value === selected) : -1;
    visibleOptions =
      selectedIndex >= VISIBLE_OPTIONS
        ? options.slice(0, VISIBLE_OPTIONS - 1).concat(options[selectedIndex])
        : options.slice(0, VISIBLE_OPTIONS);
  }

  return (
    <Stack gap="025" {...rest}>
      {visibleOptions.map((option) => {
        const isSelected = option.value === selected;
        return (
          <PlainButton
            key={option.value}
            type="button"
            data-test-integrations-filter-option={option.label}
            className={clsx(
              styles.option,
              isSelected && styles.active,
              isSelected && 'integrations-filter-option-selected',
            )}
            onClick={() => onChange(option.value)}
          >
            <Text
              text={isSelected ? 'product-p-active' : undefined}
              color={isSelected ? 'active' : undefined}
              weight={isSelected ? 'semibold' : 'regular'}
            >
              {option.label}
            </Text>
          </PlainButton>
        );
      })}
      {hasOptionsToHide ? (
        <Box px="150" py="050">
          <Link data-test-integrations-filter-show-more-or-less onClick={() => setIsShowingAll((value) => !value)}>
            {isShowingAll ? 'Show less' : 'Show more'}
          </Link>
        </Box>
      ) : null}
    </Stack>
  );
}
